import {
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Tag,
  Text,
} from "@chakra-ui/react";
import React, { memo, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { FaPlusCircle } from "react-icons/fa";
import { FaCircleMinus } from "react-icons/fa6";
import { YieldRateImprovement } from "../../contexts/types";

/**
 * 歩留改善リストの1行分のProps
 */
interface Props {
  /** 対象項目 */
  item: YieldRateImprovement;
  /** リスト内のインデックス */
  index: number;
  /** 入力エラーかどうか */
  isInvalid: boolean;
  /** エラーメッセージ（先頭行のみ表示） */
  errorMessage?: string;
  /** 選択可能な最小月 (YYYY-MM形式) */
  minMonth: string;
  /** 選択可能な最大月 (YYYY-MM形式) */
  maxMonth: string;
  /** 最終行かどうか（追加ボタン表示用） */
  isLast: boolean;
  /** リストが1件のみかどうか（削除ボタン非表示用） */
  isOnly: boolean;
  /** BOMから指定月の歩留を取得する関数（Map参照のみの軽量関数） */
  getYieldRateFromBom: (targetYearMonth: string) => number | null;
  /** 月変更時のハンドラ */
  onMonthChange: (index: number, month: string) => void;
  /** 歩留テキスト入力中の変更ハンドラ（表示テキストのみ更新） */
  onTextChange: (index: number, value: string) => void;
  /** フォーカスアウト時の確定ハンドラ */
  onBlurCommit: (index: number, value: string) => void;
  /** 行追加ハンドラ */
  onAdd: () => void;
  /** 行削除ハンドラ */
  onRemove: (index: number) => void;
}

/**
 * 歩留改善リストの1行分の入力UI
 * ※対応D: React.memo化し、他行の入力による再レンダリングを防止する
 * @param props パラメータ
 * @returns
 */
const YieldImprovementRow: React.FC<Props> = memo(({
  item,
  index,
  isInvalid,
  errorMessage,
  minMonth,
  maxMonth,
  isLast,
  isOnly,
  getYieldRateFromBom,
  onMonthChange,
  onTextChange,
  onBlurCommit,
  onAdd,
  onRemove,
}) => {
  const { t } = useTranslation();

  // console.info(`★レンダリング - 歩留改善`); // 再描画確認用

  // 選択された月のBOM歩留を取得（YYYY-MM → YYYY/MM形式に変換）、なければ0
  const bomYieldRate = useMemo(
    () => (item.month ? getYieldRateFromBom(item.month.replace("-", "/")) : null) || 0,
    [item.month, getYieldRateFromBom]
  );

  // BOM歩留を基準とした改善幅（表示用）
  const diffText = useMemo(() => {
    const diff = ((item.yieldRate * 100) - (bomYieldRate * 100)).toFixed(1);
    return diff === "-0.0" ? "0.0" : diff;
  }, [item.yieldRate, bomYieldRate]);

  // タグの色（改善幅の符号に応じて変更）
  const colorScheme = useMemo(() => {
    return diffText === "0.0" ? "gray" : parseFloat(diffText) > 0 ? "blue" : "red";
  }, [diffText]);

  return (
    <FormControl isInvalid={isInvalid}>
      <HStack>
        <Text>{t('features.InvestmentScreening.SimulationNewProduct.label.period')}</Text>
        <Input
          type="month"
          value={item.month}
          min={minMonth}
          max={maxMonth}
          onChange={(e) => onMonthChange(index, e.target.value)}
          style={{
            width: "150px",
            padding: "4px 8px",
            border: "1px solid #E2E8F0",
            borderRadius: "4px",
            fontSize: "14px",
            marginLeft: "8px",
          }}
        />
        <Text ml={2}>{t('features.InvestmentScreening.SimulationNewProduct.label.yieldImprovement')}</Text>
        <NumberInput
          width="90px"
          min={0.1}
          max={100}
          precision={1}
          step={0.1}
          value={item.month ? item.yieldRateText : "0.0"}
          clampValueOnBlur={false}
          onChange={(value) => onTextChange(index, value)}
          onBlur={() => onBlurCommit(index, item.month ? item.yieldRateText : "0.0")}
        >
          <NumberInputField placeholder="%" textAlign="right" />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <Text ml="2">%</Text>
        <Text ml="4" color="gray.600" fontSize="sm">
          {t('features.InvestmentScreening.SimulationNewProduct.label.yieldBeforeInfo', {
            value: (bomYieldRate * 100).toFixed(1)
          })}
        </Text>
        <Tag
          ml="2"
          fontWeight="bold"
          borderRadius="full"
          variant="solid"
          colorScheme={colorScheme}
        >
          {t('features.InvestmentScreening.SimulationNewProduct.label.yieldImprovementInfo', {
            value: diffText
          })}
        </Tag>
        {isLast && (
          <IconButton
            aria-label="Add"
            icon={<FaPlusCircle />}
            colorScheme="blue"
            size="sm"
            ml={2}
            onClick={onAdd}
            isDisabled={!item.month}
          />
        )}
        {!isOnly && (
          <IconButton
            aria-label="Remove"
            icon={<FaCircleMinus />}
            colorScheme="red"
            size="sm"
            ml={isLast ? 0 : 2}
            onClick={() => onRemove(index)}
          />
        )}
      </HStack>
      {index === 0 && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
    </FormControl>
  );
});

YieldImprovementRow.displayName = "YieldImprovementRow";

export default YieldImprovementRow;
